import type {
  ContentBlockT,
  ConversationMessageT,
  FileAttachmentContentBlockBodyT,
  FileAttachmentTypeT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { FileAttachmentContentBlock } from '@/entities/file-attachments';
import lotusMorphUrl from '@/shared/assets/images/LotusMorph.png';
import { formatDateTimeLabel } from '@/shared/lib';
import { ContentBlocks } from '@/shared/ui/primitives';
import { cn } from '@/lib/utils';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function createFileAttachmentRenderer(userId: string) {
  return function renderFileAttachmentBlock(block: ContentBlockT): ReactNode {
    const body = block.body;
    if (!isRecord(body)) return null;

    const attachmentId = body.attachmentId;
    if (typeof attachmentId !== 'string' || attachmentId.trim() === '') return null;

    const filenameValue = body.filename;
    const filename =
      filenameValue === null || typeof filenameValue === 'string' ? filenameValue : undefined;

    const mimeTypeValue = body.mimeType;
    const mimeType =
      mimeTypeValue === null || typeof mimeTypeValue === 'string' ? mimeTypeValue : undefined;

    const fileTypeValue = body.fileType;
    const fileType: FileAttachmentTypeT | null | undefined =
      fileTypeValue === null || fileTypeValue === 'image' || fileTypeValue === 'document'
        ? fileTypeValue
        : undefined;

    const safeBody: FileAttachmentContentBlockBodyT = {
      type: 'fileAttachment',
      attachmentId,
      filename,
      mimeType,
      fileType,
    };

    return <FileAttachmentContentBlock userId={userId} body={safeBody} />;
  };
}

export interface ChatMessageRowProps {
  userId: string;
  message: ConversationMessageT;
  patientName: string;
  highlighted?: boolean;
}

export function ChatMessageRow({ userId, message, patientName, highlighted }: ChatMessageRowProps) {
  const isUser = message.sender === 'User';
  const senderLabel = isUser ? patientName : message.sender;

  const renderersByType = useMemo(
    () => ({
      fileAttachment: createFileAttachmentRenderer(userId),
    }),
    [userId]
  );

  return (
    <div className={cn('flex gap-2 px-2', isUser ? 'flex-row' : 'flex-row-reverse')}>
      {/* Avatar */}
      {isUser ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
          <User className="h-4 w-4 text-muted-foreground" />
        </div>
      ) : (
        <img src={lotusMorphUrl} alt="Lotus" className="h-7 w-7 shrink-0 rounded-full" />
      )}

      {/* Content */}
      <div className={cn('flex max-w-[85%] flex-col', isUser ? 'items-start' : 'items-end')}>
        <span className="mb-1 text-[10px] font-medium text-muted-foreground">{senderLabel}</span>
        <div
          className={cn(
            'rounded-lg border px-3 py-2',
            highlighted && 'ring-2 ring-primary/40 ring-offset-2',
            isUser ? 'bg-secondary' : 'bg-card'
          )}
        >
          <ContentBlocks blocks={message.contentBlocks} renderersByType={renderersByType} />
        </div>
        <span className="mt-1 text-[10px] text-muted-foreground">
          {formatDateTimeLabel(message.createdAt)}
        </span>
      </div>
    </div>
  );
}
